import React, { forwardRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff, AlertCircle, CheckCircle, Search, X } from 'lucide-react';
import { cn } from '../../../../utils/cn';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  variant?: 'default' | 'filled' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  state?: 'default' | 'error' | 'success' | 'loading';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  clearable?: boolean;
  showPasswordToggle?: boolean;
  label?: string;
  description?: string;
  errorMessage?: string;
  successMessage?: string;
  className?: string;
}

const INPUT_VARIANTS = {
  default: [
    'bg-surface-primary border-neutral-300',
    'focus:border-primary-500 focus:ring-primary-500/20',
    'hover:border-neutral-400'
  ].join(' '),
  
  filled: [
    'bg-neutral-100 border-transparent',
    'focus:bg-surface-primary focus:border-primary-500 focus:ring-primary-500/20',
    'hover:bg-neutral-200'
  ].join(' '),
  
  outline: [
    'bg-transparent border-2 border-neutral-300',
    'focus:border-primary-500 focus:ring-primary-500/10',
    'hover:border-neutral-400'
  ].join(' '),
  
  ghost: [
    'bg-transparent border-transparent',
    'focus:bg-surface-primary focus:border-primary-500 focus:ring-primary-500/20',
    'hover:bg-neutral-50'
  ].join(' ')
};

const INPUT_SIZES = {
  sm: 'px-3 py-2 text-sm rounded-md',
  md: 'px-4 py-2.5 text-sm rounded-lg',
  lg: 'px-5 py-3 text-base rounded-lg'
};

const ICON_SIZES = {
  sm: 'w-4 h-4',
  md: 'w-4 h-4',
  lg: 'w-5 h-5'
};

const INPUT_STATES = {
  default: '',
  error: 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
  success: 'border-green-500 focus:border-green-500 focus:ring-green-500/20',
  loading: 'border-blue-500 focus:border-blue-500 focus:ring-blue-500/20'
};

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  variant = 'default',
  size = 'md',
  state = 'default',
  leftIcon,
  rightIcon,
  clearable = false,
  showPasswordToggle = false,
  label,
  description,
  errorMessage,
  successMessage,
  className,
  type = 'text',
  value,
  onChange,
  disabled,
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const [internalValue, setInternalValue] = useState(value || '');
  
  const inputType = showPasswordToggle && showPassword ? 'text' : type;
  const hasValue = Boolean(value || internalValue);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInternalValue(e.target.value);
    onChange?.(e);
  };
  
  const handleClear = () => {
    setInternalValue('');
    if (onChange) {
      const fakeEvent = {
        target: { value: '' },
        currentTarget: { value: '' }
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(fakeEvent);
    }
  };
  
  const getStateIcon = () => {
    switch (state) {
      case 'error':
        return <AlertCircle className={cn(ICON_SIZES[size], 'text-red-500')} />;
      case 'success':
        return <CheckCircle className={cn(ICON_SIZES[size], 'text-green-500')} />;
      case 'loading':
        return (
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className={cn(ICON_SIZES[size], 'border-2 border-blue-500 border-t-transparent rounded-full')}
          />
        );
      default:
        return null;
    }
  };
  
  const inputElement = (
    <div className="relative">
      {/* Left Icon */}
      {leftIcon && (
        <div className={cn(
          'absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none',
          ICON_SIZES[size],
          'text-neutral-400'
        )}>
          {leftIcon}
        </div>
      )}
      
      {/* Input */}
      <input
        ref={ref}
        type={inputType}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        className={cn(
          // Base styles
          'w-full border transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-offset-0',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'placeholder:text-neutral-400',
          
          // Size styles
          INPUT_SIZES[size],
          
          // Variant styles
          INPUT_VARIANTS[variant],
          
          // State styles
          INPUT_STATES[state],
          
          // Icon padding adjustments
          leftIcon && 'pl-10',
          (rightIcon || clearable || showPasswordToggle || state !== 'default') && 'pr-10',
          
          className
        )}
        {...props}
      />
      
      {/* Right Side Icons */}
      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
        {/* Clear button */}
        {clearable && hasValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className={cn(
              'text-neutral-400 hover:text-neutral-600 transition-colors',
              ICON_SIZES[size]
            )}
          >
            <X className="w-full h-full" />
          </button>
        )}
        
        {/* Password toggle */}
        {showPasswordToggle && type === 'password' && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className={cn(
              'text-neutral-400 hover:text-neutral-600 transition-colors',
              ICON_SIZES[size]
            )}
          >
            {showPassword ? <EyeOff className="w-full h-full" /> : <Eye className="w-full h-full" />}
          </button>
        )}
        
        {/* State icon */}
        {getStateIcon()}
        
        {/* Custom right icon */}
        {rightIcon && !getStateIcon() && (
          <div className={cn(ICON_SIZES[size], 'text-neutral-400')}>
            {rightIcon}
          </div>
        )}
      </div>
    </div>
  );
  
  // If no label, return just the input
  if (!label) {
    return inputElement;
  }
  
  // Return input with label and messages
  return (
    <div className="space-y-2">
      {/* Label */}
      <label className="block text-sm font-medium text-neutral-700">
        {label}
      </label>
      
      {/* Input */}
      {inputElement}
      
      {/* Description */}
      {description && !errorMessage && !successMessage && (
        <p className="text-sm text-neutral-600">{description}</p>
      )}
      
      {/* Error Message */}
      {errorMessage && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-red-600 flex items-center space-x-1"
        >
          <AlertCircle className="w-4 h-4" />
          <span>{errorMessage}</span>
        </motion.p>
      )}
      
      {/* Success Message */}
      {successMessage && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-green-600 flex items-center space-x-1"
        >
          <CheckCircle className="w-4 h-4" />
          <span>{successMessage}</span>
        </motion.p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

// Search Input Component
export interface SearchInputProps extends Omit<InputProps, 'leftIcon' | 'type'> {
  onSearch?: (value: string) => void;
  searchDelay?: number;
}

export const SearchInput: React.FC<SearchInputProps> = ({
  onSearch,
  searchDelay = 300,
  ...props
}) => {
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      onSearch?.(value);
    }, searchDelay);
    
    setSearchTimeout(timeout);
    props.onChange?.(e);
  };
  
  return (
    <Input
      type="search"
      leftIcon={<Search />}
      placeholder="Search..."
      {...props}
      onChange={handleChange}
    />
  );
};

// Textarea Component
export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  variant?: 'default' | 'filled' | 'outline' | 'ghost';
  state?: 'default' | 'error' | 'success';
  label?: string;
  description?: string;
  errorMessage?: string;
  successMessage?: string;
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  className?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({
  variant = 'default',
  state = 'default',
  label,
  description,
  errorMessage,
  successMessage,
  resize = 'vertical',
  className,
  ...props
}, ref) => {
  const textareaElement = (
    <textarea
      ref={ref}
      className={cn(
        // Base styles
        'w-full px-4 py-2.5 border transition-all duration-200',
        'focus:outline-none focus:ring-2 focus:ring-offset-0',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'placeholder:text-neutral-400 rounded-lg',
        
        // Variant styles
        INPUT_VARIANTS[variant],
        
        // State styles
        INPUT_STATES[state],
        
        // Resize styles
        resize === 'none' && 'resize-none',
        resize === 'vertical' && 'resize-y',
        resize === 'horizontal' && 'resize-x',
        resize === 'both' && 'resize',
        
        className
      )}
      {...props}
    />
  );
  
  // If no label, return just the textarea
  if (!label) {
    return textareaElement;
  }
  
  // Return textarea with label and messages
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-neutral-700">
        {label}
      </label>
      
      {textareaElement}
      
      {description && !errorMessage && !successMessage && (
        <p className="text-sm text-neutral-600">{description}</p>
      )}
      
      {errorMessage && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-red-600 flex items-center space-x-1"
        >
          <AlertCircle className="w-4 h-4" />
          <span>{errorMessage}</span>
        </motion.p>
      )}
      
      {successMessage && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-green-600 flex items-center space-x-1"
        >
          <CheckCircle className="w-4 h-4" />
          <span>{successMessage}</span>
        </motion.p>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea'; 